import { Navigate, Outlet } from 'react-router-dom';

import Loader from 'components/Loader';
import useAuth from 'hooks/useAuth';
import useMyPermissions from 'hooks/data/useMyPermissions';

// =============================================================================
// RequireAdminPanel — nested route guard for the admin panel.
// =============================================================================
//
// Sits BELOW RequireAuth (which already guarantees an authenticated session)
// and lets two kinds of login into /dashboard/admin:
//   - super admins (the SUPER_ADMIN role claim in the JWT), who get all five
//     tabs;
//   - account admins (GET /user/permissions → manages_accounts), who get the
//     Users and Settings tabs for their own account (docs/ADMIN_ACCOUNTS_PLAN.md,
//     Phase 5).
// Anyone else is sent back to the dashboard.
//
// Super admins pass at once from the token. For everyone else the answer
// comes from the API, so the guard waits for it (a Loader bar) rather than
// bouncing an account admin before the permissions request lands.
//
// This is a UX gate, not a security boundary: /admin/* is super-admin only
// and /account-admin/* checks the caller's account on every request.

export default function RequireAdminPanel() {
  const { user } = useAuth();
  const isSuperAdmin = (user?.role || '').toUpperCase() === 'SUPER_ADMIN';
  const { managesAccounts, isLoading } = useMyPermissions();

  if (isSuperAdmin || managesAccounts) return <Outlet />;
  if (isLoading) return <Loader />;
  return <Navigate to="/dashboard/default" replace />;
}
